import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  BarChart3,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  FileText,
  Building2,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  Percent,
  PieChart,
  Calendar,
  CreditCard,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL || '/_/backend'}/api`;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-NZ', { 
    style: 'currency', 
    currency: 'NZD', 
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatPercent = (value) => `${value}%`;

const loadAnalyticsData = async ({
  navigate,
  period,
  setDealerLeaderboard,
  setFunnel,
  setLenderPerformance,
  setLoading,
  setStats,
  setTimeline,
}) => {
  setLoading(true);
  try {
    const [statsRes, funnelRes, lendersRes, dealersRes, timelineRes] = await Promise.all([
      fetch(`${API}/analytics/dashboard`, { credentials: 'include' }),
      fetch(`${API}/analytics/funnel`, { credentials: 'include' }),
      fetch(`${API}/analytics/lenders/performance`, { credentials: 'include' }),
      fetch(`${API}/analytics/dealers/leaderboard?limit=5`, { credentials: 'include' }),
      fetch(`${API}/analytics/applications/timeline?period=${period}`, { credentials: 'include' })
    ]);

    if (statsRes.status === 403) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }

    if (statsRes.ok) setStats(await statsRes.json());
    if (funnelRes.ok) setFunnel(await funnelRes.json());
    if (lendersRes.ok) {
      const data = await lendersRes.json();
      setLenderPerformance(data.lenders || []);
    }
    if (dealersRes.ok) {
      const data = await dealersRes.json();
      setDealerLeaderboard(data.dealers || []);
    }
    if (timelineRes.ok) {
      const data = await timelineRes.json();
      setTimeline(data.timeline || []);
    }
  } catch (e) {
    console.error('Error fetching analytics:', e);
    toast.error('Failed to load analytics');
  } finally {
    setLoading(false);
  }
};

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [lenderPerformance, setLenderPerformance] = useState([]);
  const [dealerLeaderboard, setDealerLeaderboard] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [period, setPeriod] = useState('month');

  useEffect(() => {
    loadAnalyticsData({
      navigate,
      period,
      setDealerLeaderboard,
      setFunnel,
      setLenderPerformance,
      setLoading,
      setStats,
      setTimeline,
    });
  }, [navigate, period]);

  const StatCard = ({ title, value, subValue, icon: Icon, trend, trendValue, color = "text-black" }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2 text-sm">
          <Icon className="w-4 h-4" />
          {title}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <p className={`text-3xl font-bold ${color}`}>{value}</p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-sm text-gray-500">{subValue}</p>
          {trend && (
            <div className={`flex items-center text-sm ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              {trendValue}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-32 flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="pt-28 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div>
              <h1 className="font-outfit text-3xl font-bold text-black mb-2">
                Analytics Dashboard
              </h1>
              <p className="text-gray-600">
                Monitor performance and track key metrics
              </p>
            </div>
            <div className="flex items-center gap-3 mt-4 md:mt-0">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value="week">Last 7 days</option>
                <option value="month">Last 30 days</option>
                <option value="quarter">Last 90 days</option>
                <option value="year">Last year</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                onClick={() => loadAnalyticsData({
                  navigate,
                  period,
                  setDealerLeaderboard,
                  setFunnel,
                  setLenderPerformance,
                  setLoading,
                  setStats,
                  setTimeline,
                })}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Main Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <StatCard
                title="Total Applications"
                value={stats?.total_applications || 0}
                subValue={`${stats?.applications_this_month || 0} this month`}
                icon={FileText}
                trend={stats?.applications_trend >= 0 ? 'up' : 'down'}
                trendValue={`${Math.abs(stats?.applications_trend || 0)}%`}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <StatCard
                title="Total Loan Value"
                value={formatCurrency(stats?.total_loan_value || 0)}
                subValue={`Avg: ${formatCurrency(stats?.average_loan_amount || 0)}`}
                icon={DollarSign}
                color="text-[#00D47E]"
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <StatCard
                title="Total Users"
                value={stats?.total_users || 0}
                subValue={`${stats?.new_users_this_month || 0} new this month`}
                icon={Users}
              />
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <StatCard
                title="Commissions"
                value={formatCurrency(stats?.total_commissions || 0)}
                subValue={`${formatCurrency(stats?.pending_commissions || 0)} pending`}
                icon={CreditCard}
                color="text-purple-600"
              />
            </motion.div>
          </div>

          {/* Likelihood Distribution */}
          <div className="grid lg:grid-cols-3 gap-6 mb-8">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PieChart className="w-5 h-5 text-[#00D47E]" />
                    Approval Likelihood
                  </CardTitle>
                  <CardDescription>Distribution of applicant scores</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-green-500" />
                        <span className="text-sm">High Likelihood</span>
                      </div>
                      <span className="font-semibold">{stats?.high_likelihood_rate || 0}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-500" style={{ width: `${stats?.high_likelihood_rate || 0}%` }} />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-yellow-500" />
                        <span className="text-sm">Medium Likelihood</span>
                      </div>
                      <span className="font-semibold">{stats?.medium_likelihood_rate || 0}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-yellow-500" style={{ width: `${stats?.medium_likelihood_rate || 0}%` }} />
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-400" />
                        <span className="text-sm">Low Likelihood</span>
                      </div>
                      <span className="font-semibold">{stats?.low_likelihood_rate || 0}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full bg-red-400" style={{ width: `${stats?.low_likelihood_rate || 0}%` }} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Conversion Funnel */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="lg:col-span-2">
              <Card className="h-full">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#00D47E]" />
                    Conversion Funnel
                  </CardTitle>
                  <CardDescription>Captured leads and what they contain</CardDescription>
                </CardHeader>
                <CardContent>
                  {funnel && funnel.measures_abandonment === false && (
                    <p className="mb-4 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
                      These are counts of <strong>captured</strong> leads, not a drop-off funnel. The
                      eligibility flow saves nothing until the final step, so anyone who abandons
                      part-way leaves no record — measuring that needs step-level tracking.
                    </p>
                  )}
                  <div className="space-y-3">
                    {[
                      { label: 'Leads captured', value: funnel?.leads_captured || 0, color: 'bg-gray-300' },
                      { label: 'Contactable', value: funnel?.contactable || 0, color: 'bg-blue-400' },
                      { label: 'Employment captured', value: funnel?.employment_captured || 0, color: 'bg-purple-400' },
                      { label: 'Likely to be approved', value: funnel?.likely_approved || 0, color: 'bg-green-500' }
                    ].map((step, idx) => {
                      const maxValue = funnel?.leads_captured || 1;
                      const percentage = Math.round((step.value / maxValue) * 100);
                      return (
                        <div key={idx} className="flex items-center gap-4">
                          <span className="text-sm text-gray-600 w-40">{step.label}</span>
                          <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                            <div className={`h-full ${step.color} transition-all`} style={{ width: `${percentage}%` }} />
                          </div>
                          <span className="text-sm font-medium w-16 text-right">{step.value}</span>
                          <span className="text-xs text-gray-500 w-12 text-right">{percentage}%</span>
                        </div>
                      );
                    })}
                  </div>
                  {/* Not a funnel stage: this is every row in `users` — staff, dealers and admins
                      included — so it can legitimately exceed the lead count. Shown as a plain
                      figure so it can't render a >100% bar. */}
                  <div className="mt-4 pt-3 border-t border-gray-100 flex items-baseline justify-between">
                    <span className="text-sm text-gray-600">
                      Total accounts <span className="text-xs text-gray-400">(all roles)</span>
                    </span>
                    <span className="text-sm font-medium">{funnel?.registered_users ?? 0}</span>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Lender Performance & Dealer Leaderboard */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Lender Performance */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-[#00D47E]" />
                    Lender Performance
                  </CardTitle>
                  <CardDescription>Top lenders by application volume</CardDescription>
                </CardHeader>
                <CardContent>
                  {lenderPerformance.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No lender data yet</p>
                  ) : (
                    <div className="space-y-3">
                      {lenderPerformance.slice(0, 5).map((lender, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium">{lender.lender_name}</p>
                            <p className="text-xs text-gray-500">{lender.total_applications} applications</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-[#00D47E]">{lender.approval_rate}%</p>
                            <p className="text-xs text-gray-500">approval rate</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Dealer Leaderboard */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}>
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#00D47E]" />
                    Top Dealers
                  </CardTitle>
                  <CardDescription>By commission earned</CardDescription>
                </CardHeader>
                <CardContent>
                  {dealerLeaderboard.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No dealer data yet</p>
                  ) : (
                    <div className="space-y-3">
                      {dealerLeaderboard.map((dealer, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                              idx === 1 ? 'bg-gray-300 text-gray-700' :
                              idx === 2 ? 'bg-orange-300 text-orange-800' :
                              'bg-gray-100 text-gray-600'
                            }`}>
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium">{dealer.business_name}</p>
                              <p className="text-xs text-gray-500">{dealer.total_referrals} referrals</p>
                            </div>
                          </div>
                          <p className="font-semibold text-[#00D47E]">{formatCurrency(dealer.total_commission_earned)}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Approval Rate</p>
                  <p className="text-xl font-bold">{stats?.approval_rate || 0}%</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Active Dealers</p>
                  <p className="text-xl font-bold">{stats?.active_dealers || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Dealers</p>
                  <p className="text-xl font-bold">{stats?.total_dealers || 0}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Pending Payouts</p>
                  <p className="text-xl font-bold">{formatCurrency(stats?.pending_commissions || 0)}</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AnalyticsDashboard;
